/* eslint-disable */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from 'axios';
import config from 'src/config';
import { useAuth } from 'src/util/authenticationProvider';

type LegalItemDto = {
  id: string;
  title: string;
  category: string;
  content: string;
};

export default function LegalManager() {
  const { t } = useTranslation();
  const auth = useAuth();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<LegalItemDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // paging
  const [page, setPage] = useState<number>(1);
  const [size] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [usePaged, setUsePaged] = useState<boolean>(false);

  // create/update dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState<LegalItemDto | null>(null);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [content, setContent] = useState('');

  const baseUrl = `${config.apiUrl}/api/legal`;

  const authHeaders = auth.tokens?.accessToken
    ? { Authorization: `Bearer ${auth.tokens.accessToken}` }
    : {};

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  // helper: get URLSearchParams for form-style requests
  const toFormParams = (obj: Record<string, any>) => {
    const params = new URLSearchParams();
    Object.keys(obj).forEach(k => {
      if (obj[k] !== undefined && obj[k] !== null) params.append(k, String(obj[k]));
    });
    return params;
  };

  // normalize response data -> array of items
  const normalizeItems = (resData: any): LegalItemDto[] => {
    if (!resData) return [];
    if (Array.isArray(resData)) return resData;
    if (Array.isArray(resData.content)) return resData.content;
    if (Array.isArray(resData.data)) return resData.data;
    // fallback: maybe the API returns a single item object -> wrap it
    if (resData.id && resData.title) return [resData as LegalItemDto];
    return [];
  };

  // Load all (non-paged)
  const loadAll = async () => {
    clearMessages();
    setLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/all`, { headers: authHeaders });
      setItems(normalizeItems(res.data));
      setUsePaged(false);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to load items.');
    } finally {
      setLoading(false);
    }
  };

  // Load all paged (kept if needed)
  const loadAllPaged = async (pageIndex = 0) => {
    clearMessages();
    setLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/all/paged`, {
        headers: authHeaders,
        params: { page: pageIndex, size }
      });
      const pageObj = res.data?.data || res.data || {};
      const content = Array.isArray(pageObj?.content) ? pageObj.content : normalizeItems(pageObj);
      setItems(content);
      setTotalPages(pageObj?.totalPages || 1);
      setPage((pageObj?.number || pageIndex) + 1);
      setUsePaged(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to load paged items.');
    } finally {
      setLoading(false);
    }
  };

  // Create item (form params)
  const createItem = async () => {
    clearMessages();
    if (!title || !category || !content) {
      setError(t('legal.fillAll', 'Please fill title, category and content.'));
      return;
    }
    setLoading(true);
    try {
      const params = toFormParams({ title, category, content });
      await axios.post(`${baseUrl}/create`, params, {
        headers: { ...authHeaders, 'Content-Type': 'application/x-www-form-urlencoded' }
      });
      setSuccess(t('legal.createSuccess', 'Item created.'));
      setOpenDialog(false);
      await loadAll();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to create item.');
    } finally {
      setLoading(false);
    }
  };

  // Update item (form params)
  const updateItem = async () => {
    clearMessages();
    if (!editing || !editing.id) {
      setError('No item selected for update.');
      return;
    }
    if (!title || !category || !content) {
      setError(t('legal.fillAll', 'Please fill title, category and content.'));
      return;
    }
    setLoading(true);
    try {
      const params = toFormParams({ title, category, content });
      await axios.put(`${baseUrl}/update/${encodeURIComponent(editing.id)}`, params, {
        headers: { ...authHeaders, 'Content-Type': 'application/x-www-form-urlencoded' }
      });
      setSuccess(t('legal.updateSuccess', 'Item updated.'));
      setOpenDialog(false);
      await loadAll();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to update item.');
    } finally {
      setLoading(false);
    }
  };

  // Delete item
  const removeItem = async (id: string) => {
    clearMessages();
    setLoading(true);
    try {
      await axios.delete(`${baseUrl}/remove/${encodeURIComponent(id)}`, { headers: authHeaders });
      setSuccess(t('legal.deleteSuccess', 'Item deleted.'));
      await loadAll();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to delete item.');
    } finally {
      setLoading(false);
    }
  };

  // open dialog for create
  const openCreateDialog = () => {
    setEditing(null);
    setTitle('');
    setCategory('datenschutz'); // default
    setContent('');
    setOpenDialog(true);
  };

  // open dialog for edit
  const openEditDialog = (item: LegalItemDto) => {
    setEditing(item);
    setTitle(item.title);
    setCategory(item.category || 'datenschutz');
    setContent(item.content || '');
    setOpenDialog(true);
  };

  // add helper to strip HTML and truncate
  const excerpt = (html?: string, max = 100) => {
    if (!html) return '';
    const text = String(html).replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
    if (text.length <= max) return text;
    return text.slice(0, max).trimEnd() + '…';
  };

  useEffect(() => {
    // load existing items on page load
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">{t('legal.title', 'Legal Manager')}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" onClick={openCreateDialog}>{t('legal.create', 'Create')}</Button>
        </Box>
      </Box>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('legal.titleCol', 'Title')}</TableCell>
            <TableCell>{t('legal.categoryCol', 'Category')}</TableCell>
            <TableCell>{t('legal.contentCol', 'Content')}</TableCell>
            <TableCell>{t('legal.actions', 'Actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map(item => (
            <TableRow key={item.id}>
              <TableCell>{item.title}</TableCell>
              <TableCell>{item.category}</TableCell>
              <TableCell>{excerpt(item.content, 100)}</TableCell>
              <TableCell>
                <IconButton size="small" onClick={() => openEditDialog(item)}><EditIcon /></IconButton>
                <IconButton size="small" onClick={() => removeItem(item.id)}><DeleteIcon /></IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {usePaged && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => {
              const pageIndex = value - 1;
              setPage(value);
              loadAllPaged(pageIndex);
            }}
          />
        </Box>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="md">
        <DialogTitle>{editing ? t('legal.edit', 'Edit Item') : t('legal.create', 'Create Item')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField label={t('legal.titleCol', 'Title')} fullWidth value={title} onChange={(e) => setTitle(e.target.value)} />
            <Select
              fullWidth
              value={category}
              onChange={(e) => setCategory(String(e.target.value))}
              displayEmpty
            >
              <MenuItem value="datenschutz">datenschutz</MenuItem>
              <MenuItem value="AGB">AGB</MenuItem>
              <MenuItem value="Cookie">Cookie</MenuItem>
            </Select>
          </Box>
          <ReactQuill value={content} onChange={setContent} theme="snow" style={{ height: 300 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>{t('legal.cancel', 'Cancel')}</Button>
          <Button
            variant="contained"
            onClick={editing ? updateItem : createItem}
          >
            {editing ? t('legal.update', 'Update') : t('legal.create', 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
